import { SVGProps } from "react";

import { OrganizationType } from "~/admins/constants/organizations";
import {
  ADMIN_PERMISSIONS,
  DIRECTIONS,
  PATIENT_STATUS,
  TEAM_MEMBER_PERMISSIONS,
  TEAM_MEMBER_VALUES,
} from "~/common/constants";
import { UserTypes } from "~/common/constants/types";
import { PatientPharmacy } from "~/common/types";
import { PatientConsultation } from "~/common/types/consultation";
import {
  CommentTitleType,
  EmergencyContact,
  PatientInformation,
  Settings,
} from "~/patients/utils";
import { ProviderScheduleContactMethod } from "~/providers/types/teamMembers";
import { PatientFeed } from "~/providers/utils/types/interactions";

export interface AuthenticationResponse {
  access_token: string;
}

export interface PasswordRequirement {
  condition: string;
  valid: (password: string) => boolean;
}
export type IconProps = SVGProps<SVGSVGElement>;

export interface DateObject {
  day: string;
  month: string;
}

export interface UpdateNoteData {
  content: string | null;
}

export interface BasicUserData {
  name: string;
  last_name: string;
  type: UserTypes;
  email: string;
  id?: string;
}

export interface Patient extends BasicUserData {
  id: string;
  last_feed?: PatientFeed;
  consultation?: PatientConsultation;
  organization_patients: BasicOrganizationPatient[];
}

export interface BasicOrganizationPatient {
  patient_id: string;
  organization_id: string;
  primary_provider_id: string;
  primary_provider: TeamMemberWithOrganizationTeamMembers;
  archived_at?: string;
  notification_email_sent_at?: string;
  email_verified_at?: string;
  is_reviewed: boolean;
  settings: Settings;
  status: PatientStatus;
  patient_pharmacies: PatientPharmacy[];
  emergency_contacts: EmergencyContact[];
  patient?: BasicUserData;
}

export interface OrganizationPatientWithOrganization
  extends BasicOrganizationPatient {
  organization: Organization;
}

// eslint-disable-next-line
export interface PatientWithoutConsultation
  extends Omit<Patient, "consultation"> {}

export interface PatientBasicData extends BasicUserData {
  id: string;
  status: PatientStatus;
  archived_at?: string;
}

export type Directions = (typeof DIRECTIONS)[keyof typeof DIRECTIONS];

export type TeamMemberType =
  (typeof TEAM_MEMBER_VALUES)[keyof typeof TEAM_MEMBER_VALUES];

export type PermissionsCodes =
  | (typeof TEAM_MEMBER_PERMISSIONS)[keyof typeof TEAM_MEMBER_PERMISSIONS]
  | (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS];

export interface Permission extends InterfaceWithId {
  code: PermissionsCodes;
  description?: string;
}

export interface UserPermission extends InterfaceWithId {
  user_id: string;
  permission_id: string;
  permission: Permission;
  deleted_at?: string;
}

export interface TeamMember extends InterfaceWithId {
  email: string;
  name: string;
  last_name: string;
  phone_number: string;
  type: TeamMemberType;
  user_permissions: UserPermission[];
  preferred_reference?: string;
  profile_picture_path?: string;
  signature_name?: string;
  acuity_client_id?: string;
  invitation_code?: string;
}

export interface TeamMemberResponseToken
  extends Omit<TeamMember, "email" | "name"> {
  first_name: string;
  team_member_email: string;
}

export interface Organization {
  created_at: string;
  id: string;
  name: string;
  type: OrganizationType;
  internal_note: string;
  seats_count: number;
}

export interface OrganizationTeamMember {
  archived_at?: boolean;
  id: string;
  organization_id: string;
  team_member_id: string;
  organization: Organization;
  contact_details?: ProviderScheduleContactMethod[];
  deactivated_at?: string;
}
export interface OrganizationTeamMemberWithProvider
  extends OrganizationTeamMember {
  team_member: TeamMember;
}

export interface TeamMemberWithOrganizationTeamMembers extends TeamMember {
  organization_team_members: OrganizationTeamMember[];
}

export interface ProviderBasicData extends InterfaceWithId {
  signature_name: string;
  preferred_reference: string;
  email: string;
}

export interface InterfaceWithId {
  id: string;
}

export interface Comment {
  id: string;
  title: CommentTitleType;
  text: string;
  user: PatientInformation;
  created_at: string;
}

export interface Option {
  label: string;
  value: string;
  description?: string;
  type?: string;
  disabled?: boolean;
}

export interface GroupOptions {
  label: string;
  value: string;
}

export interface Group {
  group?: string;
  groupOptions: GroupOptions[];
}

export type PatientStatus =
  (typeof PATIENT_STATUS)[keyof typeof PATIENT_STATUS];

export const CONSULTATION_TYPES = {
  intake: "intake",
  screening: "screening",
} as const;

export type ConsultationTypes = keyof typeof CONSULTATION_TYPES;
